import type { Identifier } from "ra-core";

import type {
  DealActivityNextStepStatus,
  DealActivityResult,
  DealActivityType,
  DealStageActivity,
} from "../types";

/**
 * Almikia Epic EA, DATA CONTRACT EA frontend data layer: the single pure
 * module shared by the UI (TASK-111, TASK-112) and the FakeRest emulation
 * (`activityCatalogueProvider.ts`) -- no I/O, no React, linear in the
 * catalogue size (the ticket's own performance requirement). Covers (5)
 * attempts, (6) message placeholders, (7) the next-step preview + the
 * shared insert guards, and (10) activity/result ordering.
 */

// ---------------------------------------------------------------------------
// (6) Message placeholders
// ---------------------------------------------------------------------------

/** The 3 placeholders DATA CONTRACT EA (6) defines; a `null`/missing value is left as typed. */
export interface MessagePlaceholderValues {
  /** First name of the lead's first contact. */
  nombre?: string | null;
  /** First name of the signed-in user. */
  asesora?: string | null;
  /** Name of the lead's project. */
  proyecto?: string | null;
}

const PLACEHOLDER_PATTERN = /\{(nombre|asesora|proyecto)\}/g;

/**
 * Renders a message template per DATA CONTRACT EA (6): each placeholder is
 * filled when a value exists, left as typed (`{nombre}`) otherwise, every
 * other character is kept verbatim. The stored template itself is never
 * rewritten -- this only produces the string shown/copied in the UI.
 */
export function renderMessageTemplate(
  template: string | null | undefined,
  values: MessagePlaceholderValues,
): string {
  if (!template) return "";
  return template.replace(PLACEHOLDER_PATTERN, (match, key: string) => {
    const value = values[key as keyof MessagePlaceholderValues];
    return value != null && value !== "" ? value : match;
  });
}

// ---------------------------------------------------------------------------
// (5) Attempt cap ("Código Azul" protocol)
// ---------------------------------------------------------------------------

export interface AttemptInfo {
  /** `existingEntryCount + 1`, the attempt about to be logged. */
  attemptNumber: number;
  /** `null` when the activity has no cap. */
  cap: number | null;
  /** Whether logging this attempt would be rejected (`attemptNumber > cap`). */
  exhausted: boolean;
}

/**
 * DATA CONTRACT EA (5): a lead can hold at most `maxAttempts` activity log
 * entries of a capped activity, counted over the lead's whole history.
 * `existingEntryCount` is the count BEFORE the attempt about to be logged.
 */
export function computeAttemptInfo(
  maxAttempts: number | null | undefined,
  existingEntryCount: number,
): AttemptInfo {
  const attemptNumber = existingEntryCount + 1;
  if (maxAttempts == null) {
    return { attemptNumber, cap: null, exhausted: false };
  }
  return {
    attemptNumber,
    cap: maxAttempts,
    exhausted: attemptNumber > maxAttempts,
  };
}

/** "Intento n de N" (or just "Intento n" when the activity has no cap). */
export function formatAttemptLabel(info: AttemptInfo): string {
  return info.cap == null
    ? `Intento ${info.attemptNumber}`
    : `Intento ${info.attemptNumber} de ${info.cap}`;
}

/** The exact Spanish message DATA CONTRACT EA (5) mandates for a rejected attempt. */
export function attemptsExhaustedMessage(
  activityName: string,
  maxAttempts: number,
): string {
  return `Este lead ya llegó al máximo de intentos de ${activityName} (${maxAttempts})`;
}

// ---------------------------------------------------------------------------
// (4)/(5) Shared insert guards for an activity log entry
// ---------------------------------------------------------------------------

export const RESULT_ACTIVITY_MISMATCH_MESSAGE =
  "Este resultado no corresponde a la actividad elegida";
export const ACTIVITY_ARCHIVED_MESSAGE = "Esta actividad está archivada";
export const RESULT_ARCHIVED_MESSAGE = "Este resultado está archivado";

export interface ActivityLogEntryGuardInput {
  activityTypeId: Identifier;
  activityResultId: Identifier;
  activityTypes: readonly DealActivityType[];
  activityResults: readonly DealActivityResult[];
  /** The lead's existing entry count for `activityTypeId`, BEFORE this one. */
  existingEntryCountForActivity: number;
}

/**
 * DATA CONTRACT EA (4)/(5): the guards every insert path (direct REST insert
 * included) must enforce, in the contract's order -- the result must belong
 * to the chosen activity, neither may be archived, and the attempt cap.
 * Throws with the exact Spanish message on the first violated rule; a no-op
 * otherwise. Shared by the FakeRest `deal_notes` REST guard and the
 * `logDealActivity` emulation, so the rule is defined exactly once.
 */
export function assertActivityLogEntryAllowed(
  input: ActivityLogEntryGuardInput,
): void {
  const {
    activityTypeId,
    activityResultId,
    activityTypes,
    activityResults,
    existingEntryCountForActivity,
  } = input;

  const result = activityResults.find(
    (candidate) => candidate.id === activityResultId,
  );
  if (!result || result.activity_type_id !== activityTypeId) {
    throw new Error(RESULT_ACTIVITY_MISMATCH_MESSAGE);
  }

  const activityType = activityTypes.find(
    (candidate) => candidate.id === activityTypeId,
  );
  if (activityType?.archived_at != null) {
    throw new Error(ACTIVITY_ARCHIVED_MESSAGE);
  }
  if (result.archived_at != null) {
    throw new Error(RESULT_ARCHIVED_MESSAGE);
  }

  if (activityType?.max_attempts != null) {
    const attempt = computeAttemptInfo(
      activityType.max_attempts,
      existingEntryCountForActivity,
    );
    if (attempt.exhausted) {
      throw new Error(
        attemptsExhaustedMessage(activityType.name, activityType.max_attempts),
      );
    }
  }
}

// ---------------------------------------------------------------------------
// (7) Next-step preview
// ---------------------------------------------------------------------------

export interface NextStepPreviewInput {
  result: DealActivityResult;
  activityTypes: readonly DealActivityType[];
  /** Whether the lead has at least one contact (`deals.contact_ids[0]` exists). */
  leadHasContact: boolean;
  /**
   * The lead's activity-type ids for every entry that will exist once this
   * one is logged (i.e. including the entry about to be inserted) -- DATA
   * CONTRACT EA (7)'s "counting the entry just inserted".
   */
  priorActivityTypeIds: readonly Identifier[];
  /** Defaults to `new Date()`; injectable for deterministic tests. */
  now?: Date;
}

export interface NextStepPreview {
  status: DealActivityNextStepStatus;
  /** ISO due date for the next-step task; `null` when no task is created. */
  dueAt: string | null;
  /** The next activity's id; `null` when the result has no next step. */
  nextActivityTypeId: Identifier | null;
}

/** The result's `next_step_offset_hours` added to `now`, as an ISO string. */
export function computeDefaultNextStepDueAt(
  offsetHours: number,
  now: Date = new Date(),
): string {
  return new Date(now.getTime() + offsetHours * 60 * 60 * 1000).toISOString();
}

/**
 * DATA CONTRACT EA (7): the default next-step due date and status, in the
 * contract's exact exception order -- no next activity ('none'), the next
 * activity archived ('next_activity_archived'), the attempt cap already
 * reached for it ('attempts_exhausted'), no contact on the lead
 * ('no_contact'), otherwise the default ('created'). Pure: every input the
 * decision needs (the lead, the result, the catalogue, the lead's prior
 * entries) is passed in explicitly.
 */
export function computeNextStepPreview(
  input: NextStepPreviewInput,
): NextStepPreview {
  const {
    result,
    activityTypes,
    leadHasContact,
    priorActivityTypeIds,
    now = new Date(),
  } = input;

  if (result.next_activity_type_id == null) {
    return { status: "none", dueAt: null, nextActivityTypeId: null };
  }

  const nextActivityTypeId = result.next_activity_type_id;
  const nextActivity = activityTypes.find(
    (candidate) => candidate.id === nextActivityTypeId,
  );
  if (!nextActivity || nextActivity.archived_at != null) {
    return {
      status: "next_activity_archived",
      dueAt: null,
      nextActivityTypeId,
    };
  }

  if (nextActivity.max_attempts != null) {
    const entryCount = priorActivityTypeIds.filter(
      (id) => id === nextActivityTypeId,
    ).length;
    if (entryCount >= nextActivity.max_attempts) {
      return { status: "attempts_exhausted", dueAt: null, nextActivityTypeId };
    }
  }

  if (!leadHasContact) {
    return { status: "no_contact", dueAt: null, nextActivityTypeId };
  }

  const dueAt = computeDefaultNextStepDueAt(
    result.next_step_offset_hours ?? 0,
    now,
  );
  return { status: "created", dueAt, nextActivityTypeId };
}

// ---------------------------------------------------------------------------
// (10) Activity / result ordering
// ---------------------------------------------------------------------------

/** Ascending `(position, id)` -- the shared tie-break used everywhere in EA. */
function comparePositionThenId(
  a: { position: number; id: Identifier },
  b: { position: number; id: Identifier },
): number {
  if (a.position !== b.position) return a.position - b.position;
  return String(a.id).localeCompare(String(b.id), undefined, {
    numeric: true,
  });
}

export interface ActivityOrderingGroup {
  /** `null` when the lead's stage has no linked activity: a single, ungrouped list. */
  label: "De esta etapa" | "Otras actividades" | null;
  activities: DealActivityType[];
}

/**
 * DATA CONTRACT EA (10): non-archived activities only, grouped by whether
 * they're ENABLED-linked to the lead's current stage ("De esta etapa" first,
 * then "Otras actividades"), each group ordered by `(position, id)`. A stage
 * with no link at all (or `stageKey` unset) returns a single ungrouped list.
 */
export function orderActivitiesForStage(
  activityTypes: readonly DealActivityType[],
  stageActivities: readonly DealStageActivity[],
  stageKey: string | null | undefined,
): ActivityOrderingGroup[] {
  const sorted = activityTypes
    .filter((activity) => activity.archived_at == null)
    .slice()
    .sort(comparePositionThenId);

  const linkedIds = new Set(
    stageActivities
      .filter((link) => link.enabled && link.stage_key === stageKey)
      .map((link) => link.activity_type_id),
  );

  if (linkedIds.size === 0) {
    return [{ label: null, activities: sorted }];
  }

  return [
    {
      label: "De esta etapa",
      activities: sorted.filter((activity) => linkedIds.has(activity.id)),
    },
    {
      label: "Otras actividades",
      activities: sorted.filter((activity) => !linkedIds.has(activity.id)),
    },
  ];
}

/** DATA CONTRACT EA (10): non-archived results of `activityTypeId`, by `(position, id)`. */
export function orderResultsForActivity(
  activityResults: readonly DealActivityResult[],
  activityTypeId: Identifier,
): DealActivityResult[] {
  return activityResults
    .filter(
      (result) =>
        result.activity_type_id === activityTypeId &&
        result.archived_at == null,
    )
    .slice()
    .sort(comparePositionThenId);
}
